import React, { useEffect, useRef } from 'react';
import { X, Sparkles, Zap, BarChart2, ShieldCheck } from 'lucide-react';
import { motion } from 'framer-motion';
import { ScrollArea } from '@/components/ui/scroll-area';
import ChatMessage from './ChatMessage';
import ChatInput from './ChatInput';
import LeadConfirmation from './LeadConfirmation';

const SUGGESTIONS = [
  { icon: Zap, text: 'What can Invarya build for us?' },
  { icon: BarChart2, text: 'Tell me about your geospatial solutions' },
  { icon: ShieldCheck, text: 'I want to discuss a project' },
];

export default function ChatPanel({ onClose, messages, isLoading, leadSaved, error, onSend, onReset }) {
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  return (
    <div className="flex flex-col h-full rounded-2xl overflow-hidden shadow-[0_32px_80px_rgba(0,0,0,0.7)] border border-white/[0.07]">
      {/* Top gradient accent bar */}
      <div className="h-[2px] bg-gradient-to-r from-blue-600 via-cyan-400 to-blue-600 flex-shrink-0" />

      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3.5 bg-[#0d0d10] border-b border-white/[0.07] flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="relative">
            <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-xl flex items-center justify-center shadow-[0_0_20px_rgba(59,130,246,0.45)]">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-400 rounded-full border-2 border-[#0d0d10]" />
          </div>
          <div>
            <p className="text-[13px] font-semibold text-white leading-none tracking-tight">Invarya AI</p>
            <p className="text-[11px] text-green-400 mt-1 flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-green-400 inline-block animate-pulse" />
              Online · Replies instantly
            </p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="text-gray-500 hover:text-gray-200 transition-colors p-1.5 rounded-lg hover:bg-white/[0.07]"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Body */}
      <div className="flex flex-col flex-1 min-h-0 bg-[#09090c]">
        {leadSaved ? (
          <LeadConfirmation onReset={onReset} />
        ) : (
          <>
            <ScrollArea className="flex-1 px-4 py-3">
              {messages.length === 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.12 }}
                  className="pt-6 pb-4"
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-600/20 to-cyan-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-blue-500/20 shadow-[0_0_32px_rgba(59,130,246,0.1)]">
                    <Sparkles className="w-7 h-7 text-cyan-400" />
                  </div>
                  <h3 className="text-[13px] font-semibold text-white text-center mb-1.5">
                    How can we help you today?
                  </h3>
                  <p className="text-xs text-gray-500 text-center max-w-[210px] mx-auto leading-relaxed mb-5">
                    Our AI agent is ready to answer questions about Invarya's solutions and services.
                  </p>
                  <div className="flex flex-col gap-2">
                    {SUGGESTIONS.map(({ icon: Icon, text }) => (
                      <button
                        key={text}
                        onClick={() => onSend(text)}
                        className="flex items-center gap-2.5 w-full px-3 py-2.5 rounded-xl bg-white/[0.03] border border-white/[0.08] text-gray-300 text-xs text-left hover:bg-white/[0.07] hover:border-blue-500/30 hover:text-white transition-all group"
                      >
                        <Icon className="w-3.5 h-3.5 text-blue-400 flex-shrink-0 group-hover:text-cyan-400 transition-colors" />
                        {text}
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}

              {messages.map((msg, i) => (
                <ChatMessage key={i} message={msg} index={i} />
              ))}

              {isLoading && (
                <motion.div
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex justify-start mb-3 items-end gap-2"
                >
                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center flex-shrink-0 shadow-[0_0_12px_rgba(59,130,246,0.3)]">
                    <Sparkles className="w-3 h-3 text-white" />
                  </div>
                  <div className="bg-white/[0.05] border border-white/[0.08] rounded-2xl rounded-bl-sm px-4 py-3">
                    <div className="flex gap-1.5 items-center h-4">
                      {[0, 160, 320].map((delay) => (
                        <span
                          key={delay}
                          className="w-1.5 h-1.5 rounded-full bg-gradient-to-b from-blue-400 to-cyan-400 animate-bounce"
                          style={{ animationDelay: `${delay}ms` }}
                        />
                      ))}
                    </div>
                  </div>
                </motion.div>
              )}

              {error && (
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-red-400 text-xs text-center my-2 bg-red-500/10 border border-red-500/20 rounded-lg py-2 px-3"
                >
                  {error}
                </motion.p>
              )}

              <div ref={bottomRef} />
            </ScrollArea>

            <ChatInput onSend={onSend} disabled={isLoading} />
          </>
        )}
      </div>
    </div>
  );
}
